import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SERVICES_LIST } from "../data/siteData";

export default function ServicesSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const totalItems = SERVICES_LIST.length;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(totalItems - 1, prev + 1));
  };

  const getTransform = () => {
    if (isMobile) {
      // 280px card width + 24px gap = 304px step.
      // Half card = 140px. px-4 container padding = 16px.
      // 50vw - 16px - 140px = 50vw - 156px places the first card in the exact center of screen.
      return `translateX(calc(50vw - 156px - ${currentIndex * 304}px))`;
    }
    const step = typeof window !== "undefined" && window.innerWidth >= 1024 ? 384 : 354;
    return `translateX(-${currentIndex * step}px)`;
  };

  return (
    <section className="py-16 sm:py-24 bg-white border-t border-b border-stone-200 overflow-hidden relative">
      
      {/* Contained Header Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 sm:mb-14">
          
          {/* Header Left: Eyebrow + Headline */}
          <div className="space-y-3 max-w-2xl">
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-black text-stone-900 tracking-tight uppercase leading-tight">
              Tømrer- & Snedkeropgaver <br className="hidden sm:inline" /> udført med faglig stolthed
            </h2>
          </div>

          {/* Header Right: Pagination Counter + Circular Navigation Buttons */}
          <div className="flex items-center gap-4 self-start md:self-end">
            {/* Counter */}
            <div className="text-xs sm:text-sm font-extrabold uppercase tracking-widest text-stone-500 mr-2">
              <span className="text-stone-900">{String(currentIndex + 1).padStart(2, "0")}</span>
              <span className="mx-1 text-stone-300">/</span>
              <span>{String(totalItems).padStart(2, "0")}</span>
            </div>

            {/* Circular Prev Button */}
            <button
              type="button"
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="w-12 h-12 rounded-full border-2 border-stone-300 flex items-center justify-center text-stone-800 hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-all disabled:opacity-30 disabled:pointer-events-none cursor-pointer focus:outline-none"
              aria-label="Forrige ydelse"
            >
              <i className="fa-solid fa-arrow-left text-sm" aria-hidden="true" />
            </button>

            {/* Circular Next Button */}
            <button
              type="button"
              onClick={handleNext}
              disabled={currentIndex >= totalItems - 1}
              className="w-12 h-12 rounded-full border-2 border-stone-300 flex items-center justify-center text-stone-800 hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-all disabled:opacity-30 disabled:pointer-events-none cursor-pointer focus:outline-none"
              aria-label="Næste ydelse"
            >
              <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
            </button>
          </div>

        </div>
      </div>

      {/* Horizontal Carousel Track */}
      <div className="w-full overflow-hidden">
        {/* Container aligned with page margin on the left */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 sm:overflow-hidden">
          <div
            className="flex gap-6 transition-transform duration-500 ease-out py-2"
            style={{
              transform: getTransform(),
            }}
          >
            {SERVICES_LIST.map((service) => (
              <Link
                key={service.id}
                to={`/${service.slug}`}
                className="w-[280px] sm:w-[330px] lg:w-[360px] shrink-0 rounded-none border border-stone-200 bg-stone-50 shadow-xs hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden group cursor-pointer focus:outline-none"
              >
                {/* Visual Top Area */}
                <div className="relative aspect-[16/10] overflow-hidden bg-stone-200">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                {/* Content Area Below */}
                <div className="p-6 sm:p-7 bg-white flex-1 flex flex-col justify-between border-t border-stone-100">
                  <div>
                    <span className="text-[#E07A38] font-bold text-xs uppercase tracking-widest block mb-1">
                      Kvalitetshåndværk
                    </span>
                    <h3 className="text-xl font-black uppercase text-stone-900 tracking-tight leading-snug group-hover:text-[#E07A38] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-stone-600 text-xs sm:text-sm line-clamp-3 leading-relaxed mt-2.5 mb-6 font-normal">
                      {service.shortDesc}
                    </p>
                  </div>

                  <div className="inline-flex items-center justify-between w-full bg-stone-100 group-hover:bg-[#E07A38] text-stone-900 group-hover:text-white font-extrabold uppercase text-xs tracking-wider px-5 py-3 rounded-none transition-all">
                    <span className="flex items-center gap-2">
                      <span>Læs mere</span>
                    </span>
                    <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Callout Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14 sm:mt-18">
        <div className="bg-stone-900 text-white rounded-2xl border-t-4 border-[#E07A38] p-8 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl border-x border-b border-stone-800">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="text-xl sm:text-2xl font-black uppercase">Står du med en speciel opgave?</h3>
            <p className="text-stone-300 text-xs sm:text-sm font-normal">
              Vi finder altid en skræddersyet tømrer- eller snedkerløsning tilpasset dine specifikke ønsker og behov.
            </p>
          </div>
          <Link
            to="/kontakt"
            className="shrink-0 bg-[#E07A38] hover:bg-[#c25a1f] text-white font-extrabold uppercase tracking-wider text-xs px-7 py-3.5 rounded-md transition-all text-center shadow-md"
          >
            Kontakt Mester
          </Link>
        </div>
      </div>

    </section>
  );
}
